import { Button, Checkbox, Input, Modal, Spin, Tabs } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { FaArrowAltCircleLeft } from "react-icons/fa";
import { IoSearchOutline } from "react-icons/io5";
import {
  CHATTING,
  conversationStatus,
  conversationType,
  tabShareChat,
} from "../../config/Constant";
import { useInfoUser } from "../../store/UserStore";

import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { debounce, groupBy, sortBy } from "lodash";
import apiFactory from "../../api";
import { useChatContext } from "../../context/ChatContext";
import AvatarComponent from "../avatar/AvatarComponent";
import { BasicOrgInfo } from "../select/directory/BasicOrgInfo";
import {
  DirectoryProvider,
  useDirectoryContext,
} from "../select/directory/DirectoryContext";
import { ShareMessageDirectory } from "../select/directory/ShareMessageDirectory";

const RecentList = ({
  checkedItems,
  conversationList,
  fetchConversationsData,
  setCheckedItems,
  isLoading,
  setIsLoading,
  isMoreConversation,
  setConversationSearch,
}) => {
  const { languageMap } = useInfoUser();
  const [recentValueName, setRecentValueName] = useState("");

  const lastObserver = useRef();

  const lastConversationRef = (node) => {
    if (isLoading || !isMoreConversation) return;
    if (lastObserver.current) lastObserver.current.disconnect();

    lastObserver.current = new IntersectionObserver(async (entries) => {
      if (entries[0].isIntersecting) {
        setIsLoading(true);
        await fetchConversationsData();
      }
    });

    if (node) lastObserver.current.observe(node);
  };

  const handleCheckboxChange = (conversationId) => {
    if (checkedItems.includes(conversationId)) {
      setCheckedItems(checkedItems.filter((id) => id !== conversationId));
    } else {
      setCheckedItems([...checkedItems, conversationId]);
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((searchMemberName) => {
        setConversationSearch((prev) => ({
          ...prev,
          searchMemberName,
        }));
      }, 500),
    [],
  );

  const handleSearch = (searchMemberName) => {
    setRecentValueName(searchMemberName);
    debouncedSearch(searchMemberName);
  };

  return (
    <div>
      <Input
        value={recentValueName}
        placeholder={languageMap?.["menu.search.placeholder"] ?? "Search"}
        onChange={(e) => handleSearch(e?.target?.value)}
        prefix={<IoSearchOutline size={15} />}
        allowClear
      />
      <div className="h-[50vh] overflow-y-scroll">
        {conversationList?.map((conversation, index) => {
          if (conversation?.conversationStatus === conversationStatus.INACTIVE)
            return null;
          return (
            <div
              className="flex items-center gap-[10px] p-2"
              key={conversation?.conversationId}
              ref={
                index === conversationList?.length - 1
                  ? lastConversationRef
                  : null
              }
            >
              <Checkbox
                checked={checkedItems.includes(conversation.conversationId)}
                onChange={() =>
                  handleCheckboxChange(conversation.conversationId)
                }
              />

              <AvatarComponent conversation={conversation} />
              <div className="name-conversation font-bold">
                {conversation?.name}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const RecentCheckedList = ({
  checkedItems,
  setCheckedItems,
  setShowCheckedList,
  conversationList,
}) => {
  const { languageMap } = useInfoUser();

  const handleRemoveCheckedItem = (conversationId) => {
    setCheckedItems(checkedItems.filter((id) => id !== conversationId));
  };

  return (
    <>
      <div className="p-[12px] flex justify-center gap-[10px] items-center">
        <div className="font-semibold items-start justify-start w-full border-b text-lg flex items-center ">
          <div
            className="checked-list-icon cursor-pointer mx-4"
            onClick={() => {
              setShowCheckedList(false);
            }}
          >
            <FaArrowAltCircleLeft size={20} />
          </div>
          {languageMap?.["modal.organization.checkedList"] ??
            "Selected member list"}{" "}
        </div>
        <span
          className={
            checkedItems?.length > 30
              ? "text-[red] font-semibold"
              : "font-semibold"
          }
        >
          {checkedItems?.length}/30
        </span>
      </div>
      <div className={`p-2 overflow-y-scroll h-[54vh]`}>
        {checkedItems.map((id) => {
          const conversation = conversationList?.find(
            (conv) => conv?.conversationId === id,
          );
          return (
            <div key={id} className="flex items-center gap-[10px] p-2">
              <AvatarComponent conversation={conversation} />
              <div className="name-conversation-checked font-bold flex-1">
                {conversation?.name}
              </div>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => handleRemoveCheckedItem(id)}
              />
            </div>
          );
        })}
      </div>
    </>
  );
};

const OrgCheckedList = ({ memberGroupList }) => {
  const { languageMap } = useInfoUser();

  const { onDeleteItem, setShowCheckedList, isCheckedLoading } =
    useDirectoryContext();

  return (
    <div>
      <div className="p-[12px] flex justify-center gap-[10px] items-center">
        <div className="font-semibold items-start justify-start w-full border-b text-lg flex items-center ">
          <div
            className="checked-list-icon cursor-pointer mx-4"
            onClick={() => {
              setShowCheckedList(false);
            }}
          >
            <FaArrowAltCircleLeft size={20} />
          </div>
          {languageMap?.["modal.organization.checkedList"] ??
            "Selected member list"}{" "}
        </div>
        <span
          className={
            memberGroupList?.length > 30
              ? "text-[red] font-semibold"
              : "font-semibold"
          }
        >
          {memberGroupList?.length}/30
        </span>
      </div>
      <div className="relative overflow-y-auto h-[54vh]">
        {memberGroupList?.map((memberGroup) => (
          <BasicOrgInfo
            memberGroup={memberGroup}
            key={memberGroupList[0]?.id}
          />
        ))}
        {isCheckedLoading && (
          <div className="absolute top-[40%] w-full text-center">
            <Spin
              indicator={<LoadingOutlined className="text-[35px]" spin />}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const ShareCheckedList = ({
  activeKey,
  checkedItems,
  setCheckedItems,
  setShowCheckedList,
  conversationList,
  memberGroupList,
}) => {
  return (
    <div className="non-checked-list rounded-md w-full">
      <div className={`${activeKey === tabShareChat.ORGANIZATION && "hidden"}`}>
        <RecentCheckedList
          checkedItems={checkedItems}
          setCheckedItems={setCheckedItems}
          setShowCheckedList={setShowCheckedList}
          conversationList={conversationList}
        />
      </div>
      <div className={`${activeKey === tabShareChat.RECENT && "hidden"}`}>
        <OrgCheckedList
          memberGroupList={memberGroupList}
          setShowCheckedList={setShowCheckedList}
        />
      </div>
    </div>
  );
};

const ShareMessageModal = ({ isModalOpen, isModalClose, shareMessage }) => {
  const { languageMap } = useInfoUser();
  const { shareMessages } = useChatContext();
  const [isLoading, setIsLoading] = useState(false);
  const [lastConversationTs, setLastConversationTs] = useState(null);
  const [isMoreConversation, setIsMoreConversation] = useState(false);
  const [checkedItems, setCheckedItems] = useState([]);
  const [conversationList, setConversationList] = useState([]);
  const [orgList, setOrgList] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [activeKey, setActiveKey] = useState(tabShareChat.RECENT);
  const [conversationSearch, setConversationSearch] = useState({
    searchMemberName: null,
    labelIds: [],
    types: [
      conversationType.GROUP,
      conversationType.PERSONAL,
      conversationType.SELF,
      conversationType.SYSTEM,
    ],
  });

  const [showCheckedList, setShowCheckedList] = useState(false);

  const filterConversationTab = (key) => {
    setActiveKey(key);
  };
  const [isMobile, setIsMobile] = useState(window.innerWidth < 860);

  const memberGroupList = useMemo(() => {
    return sortBy(
      Object.values(
        groupBy(
          orgList?.filter((org) => org?.type === "PERSON" && org?.checked),
          "objectId",
        ),
      ),
      [
        (o) => {
          return o?.[0]?.position;
        },
      ],
    );
  }, [orgList]);

  const handleShareMessage = () => {
    if (activeKey === tabShareChat.RECENT) {
      shareMessages(shareMessage, checkedItems);
    } else {
      const payload = {
        userIds: orgList
          ?.filter((org) => org?.type === "PERSON" && org?.checked)
          ?.map((org) => org?.objectId),
        requestUuid: shareMessage?.requestUuid,
      };

      apiFactory.messageApi.shareMessageByUserId(payload);
    }

    closeModal();
  };

  const closeModal = () => {
    isModalClose();
  };

  const handleResize = () => {
    setIsMobile(window.innerWidth < 860);
    setShowCheckedList(window.innerWidth > 860);
  };

  const fetchConversationsData = async (isInitConversation = false) => {
    if (isLoading || (!isInitConversation && !isMoreConversation)) return;

    try {
      setIsLoading(true);

      const request = {
        isSearch: true,
        limit: CHATTING.CONVERSATION_LIMIT,
        search: {
          maxTs: isInitConversation
            ? null
            : conversationList?.[conversationList?.length - 1]?.lastMessageAt,
          isPersonal: isInitConversation
            ? true
            : conversationList?.[conversationList?.length - 1]?.type ===
              conversationType.PERSONAL,
          ...conversationSearch,
        },
      };

      const result = await apiFactory.userConversationApi.getList(request);
      if (result?.status !== 200) return;

      setConversationList((prev) =>
        isInitConversation ? result?.data : [...prev, ...(result?.data || [])],
      );
      setIsLoading(false);

      if (result?.data?.length > 0) {
        setLastConversationTs(
          result?.data[result?.data?.length - 1].lastMessageTs,
        );

        setIsMoreConversation(
          result?.data?.length === CHATTING.CONVERSATION_LIMIT,
        );
      } else {
        setIsMoreConversation(false);
      }
    } catch (e) {
      console.error("Get conversation list: ", e);
    }
  };

  const isShare = useMemo(() => {
    const list =
      activeKey === tabShareChat.RECENT ? checkedItems : memberGroupList;
    const length = list?.length ?? 0;

    return length > 0 && length <= 30;
  }, [activeKey, checkedItems?.length, memberGroupList?.length]);

  useEffect(() => {
    const items = [
      {
        key: tabShareChat.RECENT,
        label: languageMap?.["menu.tabShareChat.recent"] ?? "Recent",
        children: (
          <RecentList
            checkedItems={checkedItems}
            conversationList={conversationList}
            setConversationList={setConversationList}
            setCheckedItems={setCheckedItems}
            fetchConversationsData={fetchConversationsData}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            isMoreConversation={isMoreConversation}
            conversationSearch={conversationSearch}
            setConversationSearch={setConversationSearch}
          />
        ),
      },
      {
        key: tabShareChat.ORGANIZATION,
        label:
          languageMap?.["menu.tabShareChat.organization"] ?? "Organization",
        children: <ShareMessageDirectory />,
      },
    ];
    setTabs(items);
  }, [languageMap, checkedItems, conversationList, orgList]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (checkedItems?.length <= 0) {
      setShowCheckedList(false);
    }
  }, [checkedItems]);

  useEffect(() => {
    fetchConversationsData(true);
  }, [conversationSearch]);

  return (
    <Modal
      title={
        <div className="font-bold">
          {languageMap?.["shareMessage.title"] ?? "Share"}
        </div>
      }
      open={isModalOpen}
      onCancel={closeModal}
      footer={false}
      closeIcon={false}
      closable
      centered
      width={900}
    >
      <DirectoryProvider
        orgList={orgList}
        setOrgList={setOrgList}
        isOpen={isModalOpen}
      >
        <div className="mb-3 border-b w-[100%]" />

        <div className="flex rounded-lg check-block-mobile">
          {(!isMobile || !showCheckedList) && (
            <div className="box-search rounded-md pe-2 mt-0 mr-[12px]">
              <Tabs
                defaultActiveKey={tabShareChat.RECENT}
                activeKey={activeKey}
                items={tabs}
                onChange={filterConversationTab}
                type="line"
              />
            </div>
          )}
          {(!isMobile || showCheckedList) && (
            <ShareCheckedList
              activeKey={activeKey}
              checkedItems={checkedItems}
              setCheckedItems={setCheckedItems}
              setShowCheckedList={setShowCheckedList}
              conversationList={conversationList}
              orgList={orgList}
              memberGroupList={memberGroupList}
            />
          )}
        </div>

        <div className="flex gap-[10px] w-full justify-center mt-6">
          <Button className="bg-[#e72121] text-[white]" onClick={closeModal}>
            {languageMap?.["shareMessage.cancel"] ?? "Share"}
          </Button>
          <Button
            className="bg-[#1677ff] text-[white]"
            type="primary"
            disabled={!isShare}
            onClick={handleShareMessage}
          >
            {languageMap?.["shareMessage.share"] ?? "Share"}
          </Button>
        </div>
      </DirectoryProvider>
    </Modal>
  );
};

export { ShareMessageModal };
